import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Texto para un dato que no existe.
 *
 * Un guion suelto ("-") no dice nada: en una ficha de detalle se lee igual que
 * un separador y deja la duda de si el dato falta, no aplica o no cargo. El
 * estandar del proyecto es una frase corta en gris, y `SIN_DATO` es el default
 * generico para cualquier campo.
 *
 * Cuando el vacio SIGNIFICA algo, pasar el texto de ese caso:
 *   SIN_ASIGNAR   -> hay dato posible pero nadie lo eligio (abogado, plantilla)
 *   SIN_VINCULAR  -> no hay relacion con otro registro (oferta, cuenta)
 *   SIN_REGISTRO  -> el registro nunca se dio de alta (contrato, documento)
 */
export const SIN_DATO = 'Sin dato';
export const SIN_ASIGNAR = 'Sin asignar';
export const SIN_VINCULAR = 'Sin vinculación';
export const SIN_REGISTRO = 'Sin registro';

/** Marca de dato inexistente: mismo tono gris en toda la app. */
export function EmptyValue({ children = SIN_DATO, className }: { children?: ReactNode; className?: string }) {
  return <span className={cn('font-normal text-muted-foreground/70', className)}>{children}</span>;
}

/**
 * Devuelve el valor si trae contenido; si no, la marca de vacio.
 * Trata `''`, `'-'`, `null` y `undefined` como vacio: hay pantallas que ya
 * guardaban el guion como texto.
 */
export function orEmpty(value: ReactNode, vacio: string = SIN_DATO): ReactNode {
  if (value === null || value === undefined || value === false) return <EmptyValue>{vacio}</EmptyValue>;
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '' || t === '-' || t === '—') return <EmptyValue>{vacio}</EmptyValue>;
  }
  if (typeof value === 'number' && Number.isNaN(value)) return <EmptyValue>{vacio}</EmptyValue>;
  return value;
}
